import type { WireDraft, WireHeldSender, WireMailbox, WireThread } from '@/api/wire';
import { agoWords, daysSince } from '@/requests/explain';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';

/**
 * What a conversation, a stranger, a draft and a shared inbox say about
 * themselves (ADR-0155). Pure: rows the screens already hold, and who is
 * reading. A twin or an agent only ever prepares a draft in all of this;
 * nobody here is told that the platform sends mail, because it does not:
 * the draft is the approval, and the person presses Send.
 */

const short = (iso: string | null | undefined): string | undefined =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : undefined;

const CHANNEL: Record<WireThread['primaryChannel'], string> = {
  email: 'An email conversation',
  chat: 'A chat conversation',
  whatsapp: 'A WhatsApp conversation',
  note: 'A note',
};

/** A sender as a name, never an address; the domain says which side of the table they are on. */
function sender(who: Who, address: string | null | undefined, domain?: string | null): Party | null {
  const p = who.actor(address);
  if (!p) return null;
  if (p.kind !== 'person' || p.name === 'You') return p;
  const at = domain ?? (address && address.includes('@') ? address.split('@')[1] : null);
  return at ? { ...p, role: `writes from ${at}` } : p;
}

export interface ThreadReader {
  /** The reader's own id, to tell a claim of theirs from somebody else's. */
  humanId: string;
  /** The reader's department. */
  department: string;
  /** A draft is waiting on this conversation. */
  hasDraft: boolean;
  /** The box is a shared one, which the platform says and the address does not. */
  sharedBox: boolean;
  /** What it is filed under, by name. */
  filedUnder?: string[] | undefined;
  now?: Date | undefined;
}

export function explainThread(t: WireThread, who: Who, r: ThreadReader): ItemContext {
  const now = r.now ?? new Date();
  const subject = t.subject ?? '(no subject)';
  const path = `/mail/${t.id}`;
  const last = sender(who, t.lastFrom);
  const lastWhen = agoWords(t.lastMessageAt, now);
  const waited = daysSince(t.waitingSince, now);
  const holder = t.department ?? r.department;
  const mineClaim = t.claimedByHuman !== null && t.claimedByHuman === r.humanId;
  const otherClaim = (t.claimedByHuman !== null && !mineClaim) || t.claimedByAgent !== null;
  const noun = CHANNEL[t.primaryChannel];
  const where = t.own ? 'your own inbox' : r.sharedBox ? `a shared inbox of ${who.label(holder)}` : `${who.label(holder)}’s mail`;

  const summary = `${noun} with ${t.messageCount} message${t.messageCount === 1 ? '' : 's'} in ${where}${last ? `; the last word was from ${last.name === 'You' ? 'you' : last.name}${lastWhen ? `, ${lastWhen}` : ''}` : ''}.`;

  const need = r.hasDraft
    ? 'A decision on the reply that is drafted: send it as you (as written or edited), discard it, or have it written again.'
    : t.needsReply
      ? 'A reply, and nobody has drafted one yet.'
      : undefined;

  const why = t.own
    ? 'It is in your own inbox, which only you and your twin read; no department sees it.'
    : t.needsReply || r.hasDraft
      ? r.sharedBox
        ? mineClaim
          ? 'You claimed it, so it is on your day and off everyone else’s.'
          : otherClaim
            ? `It is in a shared inbox you work, and ${t.claimedByName ?? 'somebody'} has claimed it.`
            : 'It landed in a shared inbox you work, and nobody has claimed it yet.'
        : `It is in ${who.label(holder)}’s mail and the last word was theirs.`
      : `It is in ${who.label(holder)}’s mail, so you can follow it.`;

  const owner: Party | null | undefined = t.own
    ? { name: 'You', kind: 'person' }
    : mineClaim
      ? { name: 'You', kind: 'person' }
      : t.claimedByHuman
        ? { name: t.claimedByName || 'A colleague', kind: 'person', role: 'claimed it' }
        : t.claimedByAgent
          ? { ...((t.claimedByAgentHandle ? who.actor(t.claimedByAgentHandle) : null) ?? { name: 'An agent', kind: 'agent' as const }), role: 'claimed it' }
          : r.sharedBox
            ? null
            : undefined;

  const facts: ItemContext['facts'] = [
    ['Messages', String(t.messageCount)],
    ['Last word', t.lastFrom ? `${last?.name ?? ''}${lastWhen ? `, ${lastWhen}` : ''}` : null],
    ['Waiting for a reply since', t.waitingSince ? `${short(t.waitingSince)} (${agoWords(t.waitingSince, now)})` : null],
    ['Draft', r.hasDraft ? 'Waiting for a decision' : 'None yet'],
    ['Filed under', r.filedUnder && r.filedUnder.length > 0 ? r.filedUnder.join(', ') : null],
    ['What can be done for you', t.own ? 'Your twin can prepare a reply in your Gmail drafts; only you send it.' : 'An agent or your twin can draft a reply; only a person sends it.'],
  ];

  let next: ItemContext['next'];
  let task: ItemContext['task'];
  if (t.own) {
    if (t.needsReply) {
      next = {
        tip: 'Ask your twin in the side chat to reply: it prepares a draft in your Gmail, and only you send it.',
        instead: 'Or answer it yourself from Gmail. Nothing leaves without you either way.',
      };
      task = { title: `Reply to: ${subject}`, from: path };
    } else if (t.state === 'waiting_reply') {
      next = { tip: `Nothing is needed from you. You wrote last${waited >= 5 ? `, ${waited} days ago` : ''}, and it waits for their answer.`, instead: 'If it goes quiet, ask your twin to prepare a nudge for you to read.' };
    } else if (t.state === 'closed') {
      next = { tip: 'Nothing to do. It is closed.' };
    } else if (t.state === 'snoozed') {
      next = { tip: 'Nothing is needed now. You snoozed it, so it waits.' };
    } else {
      next = { tip: 'Nothing is needed from you. Open it to read it; ask your twin if you want it summarised.' };
    }
  } else if (r.hasDraft) {
    next = otherClaim && !mineClaim
      ? { tip: 'A reply is drafted, but somebody else has claimed this conversation. Leave the decision to them unless they hand it back.', instead: 'You can still read it and the draft from its page.', actions: [{ label: 'Read it', to: path }] }
      : {
          tip: 'A reply is drafted and waiting for you. Read it, fix anything that is off, then press Send as me — or Discard it. Nothing leaves until you do.',
          instead: 'If the tone is wrong, have it written again and say how; that is remembered for the next ones.',
          actions: [{ label: 'Read and decide', to: path, primary: true }],
        };
    task = { title: `Decide the drafted reply: ${subject}`, from: path };
  } else if (t.needsReply) {
    if (r.sharedBox && !t.claimedByHuman && !t.claimedByAgent) {
      next = {
        tip: 'Claim it first, so nobody else answers too, then ask for a draft from its page. An agent writes it; you decide it.',
        instead: 'Or leave it: it stays on the shared inbox for whoever claims it.',
        actions: [{ label: 'Open to claim it', to: path, primary: true }],
      };
    } else if (otherClaim && !mineClaim) {
      next = { tip: 'Somebody else has it. Nothing is needed from you.', instead: 'If it should be yours, ask them to hand it back.' };
    } else {
      next = {
        tip: 'It needs a reply and none is drafted. Press Draft a reply on its page, or write it yourself in Gmail; nothing is sent without you.',
        instead: 'If it is not worth an answer, leave it; nothing chases you for it.',
        actions: [{ label: 'Draft a reply', to: path, primary: true }],
      };
    }
    task = { title: `Reply to: ${subject}`, from: path };
  } else if (t.state === 'waiting_reply') {
    next = {
      tip: waited >= 5 ? `You have waited ${waited} days for their answer. Nothing is needed from you yet; if it stays quiet, ask for a draft of a nudge from its page.` : 'Waiting for their answer. Nothing is needed from you.',
      instead: 'It comes back to you on its own when they reply.',
    };
  } else if (t.state === 'snoozed') {
    next = { tip: 'Snoozed. Nothing is needed from you now.' };
  } else if (t.state === 'closed') {
    next = { tip: 'Nothing to do. It is closed.', instead: 'Open it only to read what was said.' };
  } else {
    next = { tip: 'Nothing is needed from you now. Open it to read the conversation.' };
  }

  return {
    subject: { kind: 'conversation', title: subject },
    summary,
    ...(need ? { need } : {}),
    why,
    source: { label: `${noun} in ${where}`, when: short(t.createdAt) },
    broughtBy: t.messageCount === 1 && t.lastDirection === 'inbound' && last ? { ...last, ...(short(t.createdAt) ? { when: short(t.createdAt)! } : {}) } : null,
    owner,
    knows: t.own ? undefined : who.knowers(holder),
    facts,
    next,
    open: { to: path, label: 'Open the conversation' },
    ...(task ? { task } : {}),
  };
}

/**
 * Somebody the platform does not know, who wrote. One line per sender,
 * however often they wrote; knowing or dismissing them decides every
 * message at once (ADR-0065).
 */
export function explainStranger(h: WireHeldSender, who: Who, reader: { canMint: boolean }): ItemContext {
  const p = sender(who, h.fromAddress, h.domain) ?? { name: 'Somebody', kind: 'person' as const };
  const times = h.count === 1 ? 'wrote once' : `wrote ${h.count} times`;
  const readable = !h.pastHold;
  const near = readable && h.unreadableInDays <= 5;
  const tip = h.pastHold
    ? 'The body is no longer readable, so there is little left to weigh. Dismiss it, or know them if you recognise the name so their next mail is filed.'
    : near
      ? `Decide within ${h.unreadableInDays} day${h.unreadableInDays === 1 ? '' : 's'}, or the body becomes unreadable. If you recognise them, know them; if you do business with them, know and approve.`
      : 'Do you know them? If so, know them: they become a contact and their mail is filed. Approve them only if you do business with them.';
  return {
    subject: { kind: 'sender', title: p.name },
    summary: `${p.name} ${times}${h.domain ? ` from ${h.domain}` : ''}, and the platform does not know them. The mail is held, not filed, and nothing uses it until a person decides.`,
    need: `A decision about them, which covers ${h.count === 1 ? 'their message' : `all ${h.count} of their messages`}: know them, know and approve them, or dismiss.`,
    why: h.pastHold
      ? 'It has been held past thirty days, so the body is gone and only the fact of it survives.'
      : `Held ${h.daysHeld} day${h.daysHeld === 1 ? '' : 's'}. At thirty days the body becomes unreadable${near ? `, which is in ${h.unreadableInDays} day${h.unreadableInDays === 1 ? '' : 's'}` : ''}.`,
    source: { label: h.department ? `Mail to ${who.label(h.department)}’s mailbox` : 'Mail to a mailbox the platform reads', when: short(h.heldSince) },
    broughtBy: { ...p, when: short(h.heldSince) ?? '' },
    // A held sender has no owner until a person decides; no agent may.
    owner: null,
    knows: who.knowers(h.department),
    facts: [
      ['Messages', String(h.count)],
      ['Held since', `${short(h.heldSince) ?? '—'} (${agoWords(h.heldSince) ?? 'recently'})`],
      ['Subject', h.subject ?? 'No longer readable'],
    ],
    next: {
      tip,
      instead: reader.canMint
        ? 'Or dismiss them: the fact of it stays and nothing is filed. Approving needs a one-time code, which you can make here.'
        : 'Or dismiss them: the fact of it stays and nothing is filed. Approving also needs a one-time code from a superadmin.',
    },
  };
}

/**
 * A draft waiting for a person's decision. Whoever prepared it, it is a
 * proposal: Send as me is the person's own act, and Discard throws it away
 * with nothing sent.
 */
export interface DraftReader {
  /** The thread page already shows the conversation, so it need not be linked. */
  onThread: boolean;
  /** The recipient asked not to be written to again. */
  suppressed?: boolean | undefined;
  /** The person has changed the words. */
  edited?: boolean | undefined;
  /** How many of the messages it answers the scanner flagged. */
  flagged?: number | undefined;
}

export function explainDraft(d: WireDraft, who: Who, reader: DraftReader): ItemContext {
  const handle = d.draftedByHandle ?? null;
  // `draftedBy` is an id, which says nothing; only a handle or the platform's own label names the preparer.
  const prepared = handle ? who.actor(handle) : null;
  const twin = handle?.endsWith('.twin') === true || prepared?.kind === 'twin';
  const name = d.draftedByLabel ?? prepared?.name ?? 'An agent';
  const party: Party = { name, kind: twin ? 'twin' : 'agent', ...(prepared?.to ? { to: prepared.to } : {}) };
  const names = (addresses: string[]): string => addresses.map((a) => who.actor(a)?.name ?? a).join(', ');
  const to = d.to.length > 0 ? names(d.to) : 'nobody yet';
  const title = d.subject ? `Reply: ${d.subject}` : 'A drafted reply';
  const tip = reader.suppressed
    ? 'It cannot be sent: somebody it is addressed to asked us not to write again. Discard it, or start a new one to the others.'
    : (reader.flagged ?? 0) > 0
      ? 'It was written from a message the scanner flagged. Read the reply for anything it was asked to do before you send; then press Send as me, or Discard.'
      : reader.edited
        ? 'You have changed the words. Send as me sends your version and the change is measured; Discard throws it away.'
        : 'Read it through once, check who it goes to, then press Send as me — or Discard. If the tone is off, have it written again and say how.';
  return {
    subject: { kind: 'draft', title },
    summary: `${name} wrote a reply${d.subject ? ` to “${d.subject}”` : ''}, addressed to ${to}${d.cc.length > 0 ? `, copied to ${names(d.cc)}` : ''}. It is a draft: nothing has been sent.`,
    need: 'Your decision. Send as me sends it as you, as written or with your edit; Discard throws it away; Draft again has it written the way you say.',
    why: twin
      ? `${name} prepares in your voice and never sends. The draft is the approval, and only you press Send.`
      : `${name} proposed this reply${d.department ? ` on ${who.label(d.department)}’s mail` : ''}. An agent proposes and a person decides; it goes as you, only when you press Send.`,
    source: {
      label: d.subject ? `The conversation “${d.subject}”` : 'The conversation it answers',
      ...(reader.onThread ? {} : { to: `/mail/${d.threadId}` }),
      when: short(d.createdAt),
    },
    broughtBy: { ...party, when: short(d.createdAt) ?? '' },
    owner: { name: 'You', kind: 'person', role: 'decides it' },
    knows: who.knowers(d.department),
    facts: [
      ['Prepared', agoWords(d.createdAt) ?? null],
      ['Addressed to', to],
      ['Copied', d.cc.length > 0 ? names(d.cc) : null],
      ['Also in', 'Your Gmail drafts, where you can edit it too'],
    ],
    next: {
      tip,
      instead: 'Leaving it does nothing: it waits here and in your Gmail drafts, and nothing goes out without you.',
    },
    ...(reader.onThread ? {} : { open: { to: `/mail/${d.threadId}`, label: 'Open the conversation' } }),
  };
}

/**
 * A shared inbox and who works it: an agent works it only while the ledger
 * names it and Google lists it (ADR-0106), and the half that is missing is
 * the one thing a person can fix.
 */
export function explainSharedBox(box: WireMailbox, who: Who): ItemContext {
  const dept = box.mailbox.department;
  const agents = box.members.filter((m) => m.agentId);
  const people = box.members.filter((m) => m.humanId && !m.agentId);
  const working = agents.filter((a) => a.works);
  const stalled = agents.filter((a) => !a.works);
  const label = dept ? `${who.label(dept)}’s shared inbox` : 'A shared inbox';
  return {
    subject: { kind: 'shared inbox', title: label },
    summary: `${label}: ${people.length === 0 ? 'nobody from Google’s member list has been read yet' : `${people.length} ${people.length === 1 ? 'person answers' : 'people answer'} it`}, ${agents.length === 0 ? 'and no agent is named for it' : `${working.length} of ${agents.length} ${agents.length === 1 ? 'agent works' : 'agents work'} it`}.`,
    need: stalled.length > 0 ? 'The missing half for each agent that is not working it: the ledger must name it, and Google must list it as a member.' : undefined,
    why: 'Mail to this box reaches whoever is a member; an agent proposes drafts on it and a person decides them.',
    source: { label: 'The ledger, read together with Google’s own member list' },
    broughtBy: null,
    owner: who.lead(dept),
    involved: working.length > 0 ? who.agentsOf(dept) : [],
    knows: who.knowers(dept),
    facts: [
      ['People', String(people.length)],
      ['Agents working it', String(working.length)],
      ['Agents not working it yet', stalled.length > 0 ? String(stalled.length) : null],
    ],
    next:
      stalled.length > 0
        ? {
            tip: 'An agent here is not working the box yet because one of its two halves is missing, as the list says. Only a person can fix that: ask the superadmin to add it to the ledger or to Google’s member list, whichever is missing.',
            instead: 'Until then people answer the box themselves; nothing is lost.',
          }
        : { tip: 'Nothing is needed from you. The people and agents here answer this box, and every reply is a draft a person decides.' },
  };
}
